import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Building2, Users, Calendar } from 'lucide-react';
import { formatDate } from '@/utils/helpers';

function MandaliManagement() {
  const navigate = useNavigate();
  const [mandalis, setMandalis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    manager_first_name: '',
    manager_last_name: '',
    manager_middle_name: '',
    manager_nickname: '',
    manager_contact: '',
    manager_email: '',
    manager_password: '',
  });

  useEffect(() => {
    fetchMandalis();
  }, []);

  const fetchMandalis = async () => {
    try {
      const data = await api.mandali.getAll();
      setMandalis(data);
    } catch (error) {
      toast.error('Failed to load mandalis');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.mandali.create(formData);
      toast.success('Mandali created successfully!');
      setDialogOpen(false);
      fetchMandalis();
      // Reset form
      setFormData({
        name: '',
        address: '',
        manager_first_name: '',
        manager_last_name: '',
        manager_middle_name: '',
        manager_nickname: '',
        manager_contact: '',
        manager_email: '',
        manager_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mandali');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex items-center justify-between pb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1 gradient-text">
              Mandali Management
            </h1>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              Create and manage mandali groups
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg" data-testid="create-mandali-button">
                <Plus className="w-4 h-4 mr-2" />
                Create New Mandali
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl gradient-text">Create New Mandali</DialogTitle>
                <DialogDescription>
                  Create a new mandali group with a manager account
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Mandali Details */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                    Mandali Details
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Mandali Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Enter mandali name"
                      data-testid="mandali-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Mandali operating location"
                      data-testid="mandali-address-input"
                    />
                  </div>
                </div>

                {/* Manager Details */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Manager Details
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="manager_first_name">First Name *</Label>
                      <Input
                        id="manager_first_name"
                        value={formData.manager_first_name}
                        onChange={(e) => setFormData({ ...formData, manager_first_name: e.target.value })}
                        required
                        data-testid="manager-first-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="manager_middle_name">Middle Name</Label>
                      <Input
                        id="manager_middle_name"
                        value={formData.manager_middle_name}
                        onChange={(e) => setFormData({ ...formData, manager_middle_name: e.target.value })}
                        data-testid="manager-middle-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="manager_last_name">Last Name *</Label>
                      <Input
                        id="manager_last_name"
                        value={formData.manager_last_name}
                        onChange={(e) => setFormData({ ...formData, manager_last_name: e.target.value })}
                        required
                        data-testid="manager-last-name-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="manager_nickname">Nickname</Label>
                      <Input
                        id="manager_nickname"
                        value={formData.manager_nickname}
                        onChange={(e) => setFormData({ ...formData, manager_nickname: e.target.value })}
                        placeholder="Short identifier"
                        data-testid="manager-nickname-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="manager_contact">Contact Number *</Label>
                      <Input
                        id="manager_contact"
                        value={formData.manager_contact}
                        onChange={(e) => setFormData({ ...formData, manager_contact: e.target.value })}
                        required
                        placeholder="10 digits"
                        pattern="[0-9]{10}"
                        data-testid="manager-contact-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="manager_email">Email (Optional)</Label>
                    <Input
                      id="manager_email"
                      type="email"
                      value={formData.manager_email}
                      onChange={(e) => setFormData({ ...formData, manager_email: e.target.value })}
                      placeholder="manager@example.com"
                      data-testid="manager-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="manager_password">Password *</Label>
                    <Input
                      id="manager_password"
                      type="password"
                      value={formData.manager_password}
                      onChange={(e) => setFormData({ ...formData, manager_password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Min 8 characters"
                      data-testid="manager-password-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="submit-mandali-button">
                    Create Mandali & Manager
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="skeleton h-48"></Card>
            ))}
          </div>
        ) : mandalis.length === 0 ? (
          <Card className="glass border-0 shadow-soft">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Mandalis Yet</h3>
                <p className="text-gray-600 mb-6">Create your first mandali to get started</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Mandali
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mandalis.map((mandali, index) => (
              <Card 
                key={mandali.id} 
                className="glass card-hover border-0 shadow-soft hover:shadow-medium"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`mandali-card-${mandali.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-bold text-gray-800">{mandali.name}</CardTitle>
                        <p className="text-xs text-emerald-600 font-semibold mt-1">{mandali.mandali_code}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="line-clamp-2">{mandali.address || 'No address provided'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Created: {formatDate(mandali.created_at)}</span>
                  </div>
                  
                  <div className="pt-3 border-t">
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        size="sm"
                        onClick={() => navigate('/admin/mandali')}
                      >
                        View All Mandali
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default MandaliManagement;
